/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useRef, useState } from 'react';
import { PlateReconcilationApi } from "../../../../service/plateReconcilation";
import {
  userService as plateRelatedService,
  userService,
} from "../../../../service/plateServices";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import {
  Box,
  Layers,
  CheckCircle2,
  Clock,
  AlertCircle,
  FlaskConical,
  AlertTriangle,
  QrCode,
  ChevronRight,
  Search,
  Filter,
  Download,
  FileText,
  Calendar,
  ArrowLeft,
  Activity,
  History,
  ClipboardList,
  CheckCircle,
  Zap,
  MoreVertical,
  Printer,
  ShieldMinus
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import HeaderSection from '../../../common/headerSection';
import { LuChevronDown } from 'react-icons/lu';
import CustomToast from '../../../../widgets/toaster';
import Pagination from '../../../../widgets/pagination';
import { useTranslation } from 'react-i18next';
import { helperFunctions } from '../../../../utils/helper';
import PageHeader from '../../../common/pageHeader';

// --- Types ---

interface BatchSummary {
  code: string;
  id: string;
  grn: string;
  plateType: string;
  manufacturer: string;
  mfgDate: string;
  expiryDate: string;
  qaStatus: string;
  qaApprovedDate: string;
  tcmStatus: string;
  tcmReference: string;
  totalLots: number;
  totalPlates: number;
  usedPlates: number;
  remainingPlates: number;
  activePlates: number;
  variancePlates: number;
  rejectedPlates: number;
  exceptionPlates: number;
  expiredPlates: number;
  samplingLotsCount: string;
  accountedPlates: number;
  reconPercentage: number;
  utilization: number;
  reconStatus: 'RECONCILED' | 'PENDING' | 'VARIANCE_FOUND' | 'REJECTED_PRESENT' | 'EXCEPTION_OPEN';
}

interface LotDetail {
  code: string;
  batchCode: string;
  totalPlates: number;
  usedPlates: number;
  remainingPlates: number;
  variancePlates: number;
  rejectedPlates: number;
  exceptionPlates: number;
  utilization: number;
  lastSync: string;
  samplingStatus: 'In-Process' | 'Completed' | 'Pending';
  qrGenerated: number;
  qrPending: number;
}

interface LotActivityRecord {
  activity: string;
  assigned: number;
  used: number;
  remaining: number;
  variance: number;
  rejected: number;
  exceptions: number;
  status: string;
}

interface PlateRecord {
  id: string;
  type: string;
  activityType: string;
  status: 'In Use' | 'Completed' | 'Rejected' | 'Discarded' | 'Incubating';
  stage: string;
  colonyCount: number;
  lastUpdated: string;
}

// --- Helpers ---

const getReconStatus = (remaining: number, variance: number, exceptions: number, rejected: number): BatchSummary['reconStatus'] => {
  if (exceptions > 0) return 'EXCEPTION_OPEN';
  if (rejected > 0) return 'REJECTED_PRESENT';
  if (variance > 0) return 'VARIANCE_FOUND';
  if (remaining === 0) return 'RECONCILED';
  return 'PENDING';
};

// --- Sub-Components ---

const ReconStatusBadge = ({ status }: { status: BatchSummary['reconStatus'] }) => {
  const config = {
    RECONCILED: { label: 'Reconciled', color: 'bg-emerald-500' },
    PENDING: { label: 'Pending', color: 'bg-slate-400' },
    VARIANCE_FOUND: { label: 'Variance Found', color: 'bg-orange-500' },
    REJECTED_PRESENT: { label: 'Rejected Present', color: 'bg-rose-500' },
    EXCEPTION_OPEN: { label: 'Exception Open', color: 'bg-purple-500' },
  };

  const { label, color } = config[status] || config.PENDING;

  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">{label}</span>
    </div>
  );
};

const KpiCard = ({ label, val, color, icon: Icon }: any) => {
  const colorMap: any = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    rose: 'bg-rose-50 text-rose-600 border-rose-100',
    purple: 'bg-[#fcf8fb] text-purple-600 border-purple-100/50',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    slate: 'bg-slate-50 text-slate-500 border-slate-100'
  };

  const selectedColor = colorMap[color] || colorMap.slate;

  return (
    <div className={`p-4 rounded-3xl border ${selectedColor} group transition-all hover:bg-white hover:shadow-xl hover:shadow-slate-200/50 flex flex-col gap-1 h-full`}>
      <div className="flex justify-between items-start">
        <span className="text-[10px] font-black uppercase tracking-widest leading-none group-hover:text-slate-800 transition-colors">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" />}
      </div>
      <p className="text-2xl font-black tracking-tighter transition-transform group-hover:scale-105 origin-left leading-none mt-1">{val}</p>
    </div>
  );
};

const CompactReconSummary = ({ assigned, accounted, variance, rejected, exceptions, accuracy }: any) => (
  <div className="bg-white p-4 rounded-3xl border border-slate-200/60 shadow-sm h-[110px] flex items-center justify-between">
    <div className="flex flex-col h-full justify-between py-1 border-r border-slate-100 pr-8">
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Reconciliation Summary</span>
      <div className="space-y-0">
        <p className="text-3xl font-black tracking-tighter text-slate-800 leading-none">{accuracy}%</p>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">System Accuracy</p>
      </div>
    </div>
    <div className="flex-1 grid grid-cols-5 gap-0 px-4 h-full">
      {[
        { l: 'Assigned', v: assigned, c: 'text-slate-800' },
        { l: 'Accounted', v: accounted, c: 'text-blue-600' },
        { l: 'Variance', v: variance, c: 'text-orange-500' },
        { l: 'Rejected', v: rejected, c: 'text-rose-500' },
        { l: 'Exceptions', v: exceptions, c: 'text-purple-500' }
      ].map((m, i) => (
        <div key={i} className="flex flex-col items-center justify-center border-r border-slate-50 last:border-0 h-full">
          <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.1em] mb-1">{m.l}</p>
          <p className={`text-xl font-black ${m.c} tracking-tight`}>{m.v}</p>
        </div>
      ))}
    </div>
  </div>
);

const BatchStatusBanner = ({ variance, exceptions, reconciled, pending }: any) => (
  <div className="flex flex-wrap items-center gap-3 py-1">
    {[
      { l: 'Batches With Variance', v: variance, color: 'border-orange-200 bg-orange-50 text-orange-600' },
      { l: 'Batches With Exceptions', v: exceptions, color: 'border-purple-200 bg-purple-50 text-purple-600' },
      { l: 'Batches Reconciled', v: reconciled, color: 'border-emerald-200 bg-emerald-50 text-emerald-600' },
      { l: 'Pending Reconciliation', v: pending, color: 'border-slate-200 bg-slate-50 text-slate-500' }
    ].map((s, i) => (
      <div key={i} className={`px-4 py-2 rounded-xl border ${s.color} flex items-center gap-3 transition-all hover:shadow-sm`}>
        <span className="text-[10px] font-black uppercase tracking-widest">{s.l}</span>
        <span className="w-5 h-5 flex items-center justify-center rounded-lg bg-white/50 text-[11px] font-black border border-white/50 shadow-sm">{s.v}</span>
      </div>
    ))}
  </div>
);

const DetailEntry = ({ l, v, alert, mono }: { l: string, v: string, alert?: boolean, mono?: boolean }) => (
  <div className="space-y-1 group/entry">
    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover/entry:text-blue-400 transition-colors">{l}</p>
    <p style={{ textTransform: 'capitalize' }} className={`text-[12px] font-black tracking-tight ${mono ? 'font-mono' : ''} ${alert ? 'text-amber-600' : 'text-slate-800'}`}>{v}</p>
  </div>
);

const TableContainer = ({ title, icon: Icon, children }: any) => (
  <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col">
    <div className="px-6 py-4 border-b border-slate-100 bg-white flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-800">{title}</h3>
      </div>
      {/* <button className="text-slate-300 hover:text-slate-600 transition-colors"><MoreVertical className="w-4 h-4" /></button> */}
    </div>
    <div className="overflow-x-auto overflow-y-auto max-h-[600px] audit-table-container">
      <table className="w-full border-collapse">
        {children}
      </table>
    </div>
  </div>
);

const ProgressBarCompact = ({ val, total, color }: any) => {
  const percentage = Math.min(100, Math.round((val / total) * 100));
  const colors: any = {
    blue: 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.3)]',
    emerald: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]',
    amber: 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.3)]',
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          className={`h-full ${colors[color] || colors.blue} rounded-full`}
        />
      </div>
      <span className="text-[10px] font-black text-slate-800 w-8">{percentage}%</span>
    </div>
  );
};

const ActivityDonutChart = ({ data }: { data: any[] }) => {
  const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#8B5CF6', // Violet
  '#EF4444', // Red
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#6366F1', // Indigo
  '#A855F7', // Purple
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#78716C', // Stone
];

  return (
    <div className="bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm space-y-6 flex flex-col h-full min-h-[440px]">
      <div className="flex items-center justify-between border-b border-slate-50 pb-3">
        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Activity-wise Utilization</h3>
        <Activity className="w-4 h-4 text-slate-300" />
      </div>
      <div className="flex-1 w-full h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={100}
              paddingAngle={5}
              dataKey="value"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
              itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
            />
            {/* <Legend
              verticalAlign="bottom"
              height={80}
              iconType="circle"
              formatter={(value) => <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{value}</span>}
            /> */}
            <Legend
              content={({ payload }) => (
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 mt-2">
                  {payload?.map((entry, index) => (
                    <div
                      key={index}
                      className="flex items-center whitespace-nowrap"
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-1"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="text-[10px] font-bold uppercase">
                        {entry.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// --- Main View ---

export default function PlateReconciliation() {

  const { t } = useTranslation();
  const currentDate = new Date().toISOString().split('T')[0];
  const dropdownRef = useRef<any>(null);

  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'BATCH' | 'LOT' | 'ACTIVITY'>('OVERVIEW');
  const [selectedBatchCode, setSelectedBatchCode] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<any>({});
  const [selectedLotCode, setSelectedLotCode] = useState<string | null>(null);
  const [selectedLot, setSelectedLot] = useState<any>({});

  // Batch list (Overview table)
  const [batchListRaw, setBatchListRaw] = useState<BatchSummary[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState('');
  const [plateBatches, setPlateBatches] = useState<Array<{ value: string; label: string }>>([]);
  const [fromdate, setFromDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(currentDate);
  const [sort] = useState('id');
  const [order] = useState('desc');
  const [useEffectRequired, setUseEffectRequired] = useState(false);

  // Summary stats (OVERVIEW KPIs)
  const [summaryStats, setSummaryStats] = useState<any>({});

  // Batch stats (BATCH tab header)
  const [batchStats, setBatchStats] = useState<any>({});

  // Lot list (BATCH tab table & LOT tab)
  const [lotListRaw, setLotListRaw] = useState<LotDetail[]>([]);
  const [lotWisePage, setLotWisePage] = useState(1);
  const [lotWisePageSize, setLotWisePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [lotWiseTotalPages, setLotWiseTotalPages] = useState(0);
  const [batchid, setBatchId] = useState('');

  // Activity wise list (LOT tab activity table)
  const [activityWiseListRaw, setActivityWiseListRaw] = useState<LotActivityRecord[]>([]);
  const [activityWisePage, setActivityWisePage] = useState(1);
  const [activityWisePageSize, setActivityWisePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [activityWiseTotalPages, setActivityWiseTotalPages] = useState(0);
  const [activityConciliationType, setActivityConciliationType] = useState('');
  const [platesUsedTotal, setPlatesUsedTotal] = useState<string[]>([]);
  const [batchWiseReTotal, setBatchWiseReTotal] = useState<string[]>([]);

  // Remaining plates list (ACTIVITY tab)
  const [remainingPlatesRaw, setRemainingPlatesRaw] = useState<LotDetail[]>([]);
  const [plateWisePage, setPlateWisePage] = useState(1);
  const [plateWisePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [plateWiseTotalPages, setPlateWiseTotalPages] = useState(0);
  const [remainingPlatesTotal, setRemainingPlatesTotal] = useState<string[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");


  // Current selected detail objects
  const currentBatch: BatchSummary = batchListRaw.find(b => b.code === selectedBatchCode) || batchListRaw[0] || {
    code: '', grn: '', plateType: '', manufacturer: '', mfgDate: '', expiryDate: '',
    qaStatus: '', qaApprovedDate: '', tcmStatus: '', tcmReference: '',
    totalLots: 0, totalPlates: 0, usedPlates: 0, remainingPlates: 0,
    activePlates: 0, variancePlates: 0, rejectedPlates: 0, exceptionPlates: 0,
    expiredPlates: 0, samplingLotsCount: '0/0', accountedPlates: 0,
    reconPercentage: 0, utilization: 0, reconStatus: 'PENDING'
  };
  const currentLot: LotDetail = lotListRaw.find(l => l.code === selectedLotCode) || lotListRaw[0] || {
    code: '', batchCode: '', totalPlates: 0, usedPlates: 0, remainingPlates: 0,
    variancePlates: 0, rejectedPlates: 0, exceptionPlates: 0, utilization: 0,
    lastSync: '', samplingStatus: 'Pending', qrGenerated: 0, qrPending: 0
  };

  const handleSelectActivityTab = (id: string) => {
    setActiveTab(id as any)
    if (id === "BATCH" && selectedBatch) {
      handleBatchRowClick(selectedBatch)
    }
    if (id === "LOT" && selectedLot) {
      handleLotInvestigate(selectedLot)
    }
    if (id === "ACTIVITY" && selectedBatch) {
      handleBatchRemainingClick(selectedBatch)
    }
  }

  // --- API Functions ---

  const getBatchList = async () => {
    try {
      const response = await PlateReconcilationApi.getBatchList(
        page, pageSize, selectedBatchFilter, sort, order, fromdate, toDate
      );
      if (response.status && response.statusCode === 200) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setTotalPages(response?.totalPages);
          const rowData: BatchSummary[] = response.data.map((element: any) => ({
            code: element.batch_no,
            grn: element.batch_grn || '-',
            // plateType: element.plate_type || '-',
            plateType: element.plate_size_type_name || '-',
            // manufacturer: element.manufacturer || '-',
            manufacturer: element.manufacturer_details || '-',
            // mfgDate: element.mfg_date || '-',
            mfgDate: element.manufacturing_date || '-',
            // expiryDate: element.expiry_date || '-',
            expiryDate: element.expiry_date || element.batch_expiry_date || '-',
            // qaStatus: element.qa_status || '-',
            qaStatus: element.qa_approved_status || '-',
            qaApprovedDate: element.qa_approved_date || '-',
            tcmStatus: element.tcm_status || '-',
            // tcmReference: element.tcm_reference || '-',
            tcmReference: element.tcm_ref || '-',
            totalLots: element.lot_count,
            totalPlates: element.plate_count,
            usedPlates: element.used_plates_count,
            remainingPlates: element.remaining_plates_count,
            activePlates: element.active_plates_count || 0,
            variancePlates: element.variance_plates_count || 0,
            rejectedPlates: element.rejected_plates_count || 0,
            exceptionPlates: element.exception_plates_count || 0,
            expiredPlates: element.expired_plates_count || 0,
            samplingLotsCount: `${element.sampling_lots || 0} / ${element.lot_count}`,
            accountedPlates: element.used_plates_count,
            reconPercentage: element.plate_count > 0
              ? Math.round((element.used_plates_count / element.plate_count) * 1000) / 10
              : 0,
            utilization: element.plate_count > 0
              ? Math.round((element.used_plates_count / element.plate_count) * 1000) / 10
              : 0,
            reconStatus: getReconStatus(
              element.remaining_plates_count,
              element.variance_plates_count || 0,
              element.exception_plates_count || 0,
              element.rejected_plates_count || 0
            ),
            id: element.id,
          }));
          setBatchListRaw(rowData);
          // if (!selectedBatchCode && rowData.length > 0) {
          if (rowData.length > 0) {
            setSelectedBatchCode(rowData[0].code);
            setSelectedBatch(rowData[0])
          }
        } else {
          setBatchListRaw([]);
        }
      } else {
        setBatchListRaw([]);
      }
    } catch (error) {
      console.error('getBatchList error', error);
    }
  };

  const listPlateBatch = async () => {
    try {
      const response = await plateRelatedService.listPlateBatch();
      if (response.status && response.statusCode === 200) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          const rowData = response.data.map((element: any) => ({
            value: element.batch_number,
            label: element.batch_number,
          }));
          setPlateBatches(rowData);
        }
      }
    } catch (error) {
      console.error('listPlateBatch error', error);
    }
  };

  const getReconciliationBatcheStat = async (batchNo: string) => {
    try {
      const response = await PlateReconcilationApi.getReconciliationBatcheStat(batchNo);
      if (response.status && response.statusCode === 200) {
        setSummaryStats({
          totalBatches: response.data.total_batches,
          totalPlates: response.data.total_plates,
          totalPlatesUsed: response.data.used_plates,
          totalPlatesRemaining: response.data.remaining_plates,
        });
      }
    } catch (error) {
      console.error('getReconciliationBatcheStat error', error);
    }
  };

  const getBatcheStat = async (batchNo: string) => {
    try {
      const response = await PlateReconcilationApi.getReconciliationBatcheStat(batchNo);
      if (response.status && response.statusCode === 200) {
        const element = response.data.batch_details;
        setBatchStats({
          batchName: element?.batch_no,
          batchGrn: element?.batch_grn,
          totalPlates: response.data.total_plates,
          totalPlatesUsed: response.data.used_plates,
          totalPlatesRemaining: response.data.remaining_plates,
        });
        setBatchWiseReTotal([
          'Total', '-',
          response.data.total_plates.toString(),
          response.data.used_plates.toString(),
          response.data.remaining_plates.toString(),
        ]);
      }
    } catch (error) {
      console.error('getBatcheStat error', error);
    }
  };

  const getLotList = async (pg: number, pgSize: number, bId: any) => {
    try {
      const response = await PlateReconcilationApi.ListLots(pg, pgSize, bId, '', 'failed,passed,pending,Not required');
      if (response.status && response.statusCode === 200) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setLotWiseTotalPages(response?.totalPages);
          const rowData: LotDetail[] = response.data.map((element: any) => ({
            code: element.lot_code,
            batchCode: element.batch_no || '',
            totalPlates: element.plate_count,
            usedPlates: element.used_plates_count,
            remainingPlates: element.remaining_plates_count,
            variancePlates: element.variance_plates_count || 0,
            rejectedPlates: element.rejected_plates_count || 0,
            exceptionPlates: element.exception_plates_count || 0,
            utilization: element.plate_count > 0
              ? Math.round((element.used_plates_count / element.plate_count) * 100)
              : 0,
            lastSync: element.last_sync || '-',
            samplingStatus: element.sampling_status || 'Pending',
            qrGenerated: element.qr_generated_new_plates || 0,
            qrPending: element.remaining_plates_for_qr || 0,
            id: element.id,
          }));
          setLotListRaw(rowData);
          setSelectedLot(rowData[0]);
        } else {
          setLotListRaw([]);
        }
      } else {
        setLotListRaw([]);
      }
    } catch (error) {
      console.error('getLotList error', error);
    }
  };

  const getRemainingLotList = async (pg: number, pgSize: number, bId: any, lotId?: any) => {
    try {
      const response = await PlateReconcilationApi.ListLots(pg, pgSize, bId, lotId || '', 'failed,passed,pending,Not required');
      if (response.status && response.statusCode === 200) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setPlateWiseTotalPages(response?.totalPages);
          const rowData: LotDetail[] = response.data.map((element: any) => ({
            code: element.lot_code,
            batchCode: element.batch_no || '',
            totalPlates: element.plate_count,
            usedPlates: element.used_plates_count,
            remainingPlates: element.remaining_plates_count,
            variancePlates: 0,
            rejectedPlates: 0,
            exceptionPlates: 0,
            utilization: element.plate_count > 0
              ? Math.round((element.used_plates_count / element.plate_count) * 100)
              : 0,
            lastSync: '-',
            samplingStatus: 'Pending',
            qrGenerated: element.qr_generated_new_plates || 0,
            qrPending: element.remaining_plates_for_qr || 0,
            id: element.id,
          }));
          setRemainingPlatesRaw(rowData);
        } else {
          setRemainingPlatesRaw([]);
        }
      } else {
        setRemainingPlatesRaw([]);
      }
    } catch (error) {
      console.error('getRemainingLotList error', error);
    }
  };

  const getActivityConcilation = async (pg: number, pgSize: number, id: any, type: string) => {
    setActivityConciliationType(type);
    try {
      let response: any;
      if (type === 'batchId') {
        response = await PlateReconcilationApi.getActivityConcilation(pg, pgSize, id);
      } else if (type === 'lotId') {
        response = await PlateReconcilationApi.getActivityConcilationByLotId(pg, pgSize, id);
      }
      if (response?.status && response.statusCode === 200) {
        if (response.data && response.data.length > 0) {
          setActivityWiseTotalPages(response?.totalPages);
          const rowData: LotActivityRecord[] = response.data.map((element: any) => ({
            activity: element.lot_code,
            assigned: element.activity_wise_reconciliation?.sampling_plate_count || 0,
            used: element.activity_wise_reconciliation?.active_air_sampling_plate_count || 0,
            remaining: element.activity_wise_reconciliation?.passive_air_sampling_plate_count || 0,
            variance: element.activity_wise_reconciliation?.anaerobic_monitoring_plate_count || 0,
            rejected: element.activity_wise_reconciliation?.rejected_plate_count || 0,
            exceptions: element.activity_wise_reconciliation?.exception_added_plate_count || 0,
            status: (element.activity_wise_reconciliation?.rejected_plate_count || 0) > 0 ? 'Variance Found' : 'Released',
            id: element.id,
            // full breakdown fields for the detailed table
            plate_sampling: element.activity_wise_reconciliation?.sampling_plate_count,
            air_sampling: element.activity_wise_reconciliation?.active_air_sampling_plate_count,
            passive_sampling: element.activity_wise_reconciliation?.passive_air_sampling_plate_count,
            anaerobic_monitoring: element.activity_wise_reconciliation?.anaerobic_monitoring_plate_count,
            nitrogen_monitoring: element.activity_wise_reconciliation?.nitrogen_monitoring_plate_count,
            compressed_air_monitoring: element.activity_wise_reconciliation?.compressed_air_monitoring_plate_count,
            sample_type_test: element.activity_wise_reconciliation?.raw_material_plate_count,
            surface_monitoring: element.activity_wise_reconciliation?.sf_monitoring_plate_count,
            glove_monitoring: element.activity_wise_reconciliation?.glove_monitoring_plate_count,
            personnel_monitoring: element.activity_wise_reconciliation?.personal_monitoring_plate_count,
            swab_collection: element.activity_wise_reconciliation?.sf_monitoring_swab_plate_count,
            visual_inspection_failed: element.activity_wise_reconciliation?.visual_inspection_failed_plate_count,
            plate_rejected: element.activity_wise_reconciliation?.rejected_plate_count,
            plate_discarded: element.activity_wise_reconciliation?.discarded_plate_count,
            exception_added: element.activity_wise_reconciliation?.exception_added_plate_count,
          }));
          setActivityWiseListRaw(rowData);
        } else {
          setActivityWiseListRaw([]);
        }
      } else {
        setActivityWiseListRaw([]);
      }
    } catch (error) {
      console.error('getActivityConcilation error', error);
    }
  };

  const getActivityWiseReconciliationStat = async (batchId: string, lotId: string) => {
    try {
      const response = await PlateReconcilationApi.getActivityWiseReconciliationStat(batchId, lotId);
      if (response.status && response.statusCode === 200) {
        setPlatesUsedTotal([
          'Total', '-',
          response.data.sampling_plate_count.toString(),
          response.data.active_air_sampling_plate_count.toString(),
          response.data.passive_air_sampling_plate_count.toString(),
          response.data.anaerobic_monitoring_plate_count.toString(),
          response.data.nitrogen_monitoring_plate_count.toString(),
          response.data.compressed_air_monitoring_plate_count.toString(),
          response.data.raw_material_plate_count.toString(),
          response.data.sf_monitoring_plate_count.toString(),
          response.data.glove_monitoring_plate_count.toString(),
          response.data.personal_monitoring_plate_count.toString(),
          response.data.sf_monitoring_swab_plate_count.toString(),
          response.data.visual_inspection_failed_plate_count.toString(),
          response.data.rejected_plate_count.toString(),
          response.data.discarded_plate_count.toString(),
          response.data.exception_added_plate_count.toString(),
        ]);
      }
    } catch (error) {
      console.error('getActivityWiseReconciliationStat error', error);
    }
  };

  const getRemainingReconciliationStat = async (batchId: string, type?: string) => {
    try {
      let response: any;
      if (type === 'lotWise') {
        response = await PlateReconcilationApi.getRemainingReconciliationStat('', batchId);
      } else {
        response = await PlateReconcilationApi.getRemainingReconciliationStat(batchId, '');
      }
      if (response?.status && response.statusCode === 200) {
        setRemainingPlatesTotal([
          'Total', '-',
          response.data.total_qr_generated_new_plates.toString(),
          response.data.total_remaining_plates_for_qr.toString(),
        ]);
      }
    } catch (error) {
      console.error('getRemainingReconciliationStat error', error);
    }
  };

  // --- Interaction Handlers ---

  const handleBatchRowClick = (batch: BatchSummary) => {
    setSelectedBatchCode(batch.code);
    setSelectedBatch(batch);
    getBatcheStat(batch.code);
    getLotList(1, DEFAULT_PAGE_SIZE, (batch as any).id);
    setLotWisePage(1);
    setBatchId((batch as any).id);
    setActivityWisePage(1);
    getActivityConcilation(1, DEFAULT_PAGE_SIZE, (batch as any).id, 'batchId');
    getActivityWiseReconciliationStat((batch as any).id, '');
    setActiveTab('BATCH');
  };

  const handleLotInvestigate = (lot: LotDetail) => {
    setSelectedLotCode(lot.code);
    getActivityConcilation(1, DEFAULT_PAGE_SIZE, (lot as any).id, 'lotId');
    setActivityWisePage(1);
    getActivityWiseReconciliationStat('', (lot as any).id as any);
    setActiveTab('LOT');
  };

  const handleBatchPlatesUsedClick = (batch: BatchSummary) => {
    setSelectedBatchCode(batch.code);
    getBatcheStat(batch.code);
    getActivityConcilation(1, DEFAULT_PAGE_SIZE, (batch as any).id, 'batchId');
    setActivityWisePage(1);
    setBatchId((batch as any).id);
    getActivityWiseReconciliationStat((batch as any).id, '');
    setActiveTab('LOT');
  };

  const handleBatchRemainingClick = (batch: BatchSummary) => {
    setSelectedBatchCode(batch.code);
    setSelectedBatch(batch);
    getBatcheStat(batch.code);
    getRemainingLotList(1, DEFAULT_PAGE_SIZE, (batch as any).id);
    setPlateWisePage(1);
    setBatchId((batch as any).id);
    getRemainingReconciliationStat((batch as any).id);
    setActiveTab('ACTIVITY');
  };

  //download reconceliation report
  const downloadAuditCSV = async () => {
    try {
      const response = await PlateReconcilationApi.downloadReconceliationReport(selectedBatchFilter);
      if (response.status && response.statusCode === 200) {
        setToastMessage(response.message)
        setToastType("success")
      } else {
        setToastMessage(response.message)
        setToastType("error")
      }
      setShowToast(true)
    } catch (error) {
      console.error("Plate response", error);
    }
  };

  const handleFilterApply = () => {
    setPage(1);
    setUseEffectRequired(true);
  };

  const handleLotWisePageChange = (newPage: number, pgSize?: number) => {
    setLotWisePage(newPage);
    getLotList(newPage, pgSize || lotWisePageSize, batchid);
  };

  // const handleActivityWisePageChange = (newPage: number, pgSize?: number) => {
  //   setActivityWisePage(newPage);
  //   getActivityConcilation(newPage, pgSize || activityWisePageSize, batchid, activityConciliationType || 'batchId');
  // };

  const handlePlateWisePageChange = (newPage: number, pgSize?: number) => {
    setPlateWisePage(newPage);
    getRemainingLotList(newPage, pgSize || plateWisePageSize, batchid);
  };

  const handlePageChange = (newPage: any, pageSize?: number) => {
    setPage(newPage);
    if (pageSize) {
      setPageSize(pageSize);
    }
    setUseEffectRequired(true);
  };

  //handle lot wise table pagination 
  const handleLotWisePageChangeLot = (newPage: any, pageSize?: number) => {
    setLotWisePage(newPage);
    if (pageSize) {
      setLotWisePageSize(pageSize)
      getLotList(newPage, pageSize, batchid)
    } else {
      getLotList(newPage, lotWisePageSize, batchid)
    }
  };

  //handle activity wise table pagination 
  const handleActivityWisePageChange = (newPage: any, pageSize?: number) => {
    setActivityWisePage(newPage);
    if (pageSize) {
      setActivityWisePageSize(pageSize)
      getActivityConcilation(newPage, pageSize, batchid, "batchId")
    } else {
      getActivityConcilation(newPage, activityWisePageSize, batchid, "batchId")
    }
  };

  // --- Effects ---

  useEffect(() => {
    listPlateBatch();
    getReconciliationBatcheStat('');
  }, []);

  useEffect(() => {
    getBatchList();
    setUseEffectRequired(false);
  }, [useEffectRequired, page]);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Derive computed summary values from real data
  const totalInventory = summaryStats.totalPlates || 0;
  const totalUsed = summaryStats.totalPlatesUsed || 0;
  const totalRemaining = summaryStats.totalPlatesRemaining || 0;
  const totalBatches = summaryStats.totalBatches || 0;

  // Aggregate variance/rejected/exceptions across loaded batches for overview banner
  const batchesWithVariance = batchListRaw.filter(b => b.variancePlates > 0).length;
  const batchesWithExceptions = batchListRaw.filter(b => b.exceptionPlates > 0).length;
  const batchesReconciled = batchListRaw.filter(b => b.reconStatus === 'RECONCILED').length;
  const batchesPending = batchListRaw.filter(b => b.reconStatus === 'PENDING').length;

  const totalVariance = batchListRaw.reduce((s, b) => s + b.variancePlates, 0);
  const totalRejected = batchListRaw.reduce((s, b) => s + b.rejectedPlates, 0);
  const totalExceptions = batchListRaw.reduce((s, b) => s + b.exceptionPlates, 0);
  const totalExpired = batchListRaw.reduce((s, b) => s + b.expiredPlates, 0);
  const totalActive = batchListRaw.reduce((s, b) => s + b.activePlates, 0);
  const totalAccounted = batchListRaw.reduce((s, b) => s + b.accountedPlates, 0);
  // const overallAccuracy = totalInventory > 0
  //   ? Math.round((totalAccounted / totalInventory) * 1000) / 10
  //   : 0;
  const overallAccuracy = 100;

  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  // Lot-level activity chart data derived from activityWiseListRaw
  const activityChartData =
    activityWiseListRaw.length > 0
      ? [
        {
          name: "Air Sampling",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.air_sampling || 0),
            0
          ),
        },
        {
          name: "Passive Sampling",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.passive_sampling || 0),
            0
          ),
        },
        {
          name: "Anaerobic Monitoring",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.anaerobic_monitoring || 0),
            0
          ),
        },
        {
          name: "Nitrogen Monitoring",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.nitrogen_monitoring || 0),
            0
          ),
        },
        {
          name: "Compressed Air",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.compressed_air_monitoring || 0),
            0
          ),
        },
        {
          name: "Raw Material Test",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.sample_type_test || 0),
            0
          ),
        },
        {
          name: "Contact Plate Monitoring",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.surface_monitoring || 0),
            0
          ),
        },
        {
          name: "Glove Monitoring",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.glove_monitoring || 0),
            0
          ),
        },
        {
          name: "Personnel Monitoring",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.personnel_monitoring || 0),
            0
          ),
        },
        {
          name: "Plate Sampling",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.plate_sampling || 0),
            0
          ),
        },
        {
          name: "Swab Collection",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.swab_collection || 0),
            0
          ),
        },
        {
          name: "Visual Insp. Failed",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.visual_inspection_failed || 0),
            0
          ),
        },
        {
          name: "Plate Rejected",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.plate_rejected || 0),
            0
          ),
        },
        {
          name: "Plate Discarded",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.plate_discarded || 0),
            0
          ),
        },
        {
          name: "Exception Added",
          value: activityWiseListRaw.reduce(
            (s: number, r: any) => s + (r.exception_added || 0),
            0
          ),
        },
      ].filter((d) => d.value > 0)
      : [{ name: "No Data", value: 1 }];

  // Activity tab QR stats
  const totalQrGenerated = remainingPlatesRaw.reduce((s, l) => s + l.qrGenerated, 0);
  const totalQrPending = remainingPlatesRaw.reduce((s, l) => s + l.qrPending, 0);
  const totalRemainingInActivity = remainingPlatesRaw.reduce((s, l) => s + l.remainingPlates, 0);

  return (
    <div>
      <HeaderSection />
      <PageHeader pageHeader={t('reconciliationSummary.reconciliationSummary')} pageDescription={t('pageDescription.reconciliationSummary.description')} />
      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={5000}
        message={toastMessage}
        toastType={toastType}
      />
      <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-900">
        {/* Top Header */}
        <header className="sticky top-0 z-30 px-6 py-4 bg-white backdrop-blur-xl border-b border-slate-200">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-8">
            <div className="flex items-center gap-5">
              <div className="hidden lg:flex items-center gap-2">
                <div className="w-10 h-10 rounded-2xl bg-[#0F172A] flex items-center justify-center text-white shadow-lg shadow-slate-900/10">
                  <Layers className="w-5 h-5" />
                </div>
              </div>
              <div className="space-y-0.5">
                <h1 className="text-xl font-semibold tracking-tighter text-slate-900 leading-none">Plate Reconciliation</h1>
                {/* <p className="mt-1 text-[14px] font-normal text-gray-600 leading-5">
                  {t('pageDescription.reconciliationSummary.description')}
                </p> */}
                {/* <nav className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-blue-600 transition-colors" onClick={() => setActiveTab('OVERVIEW')}>Zent EM</span>
                  <ChevronRight className="w-3 h-3 text-slate-300" />
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Inventory Management</span>
                </nav> */}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-semibold shadow-sm hover:border-blue-200 transition-all">
                <Printer className="w-4 h-4" />
                <span className="hidden md:inline uppercase tracking-widest">Print Audit Log</span>
              </button> */}
              <button
                onClick={downloadAuditCSV}
                disabled={!selectedBatchFilter}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-semibold border transition-all ${selectedBatchFilter
                  ? "bg-[#0F172A] text-white border-slate-800 shadow-xl shadow-slate-900/10 hover:bg-slate-800 cursor-pointer"
                  : "bg-slate-200 text-slate-500 border-slate-300 cursor-not-allowed opacity-60"
                  }`}
              >
                <Download className="w-4 h-4" />
                <span className="hidden md:inline uppercase tracking-widest">
                  Export Reconciliation
                </span>
              </button>
            </div>
          </div>
        </header>

        {/* Filter Section */}
        <section className="bg-white border-b border-slate-200/60 px-6 py-4">
          <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-4">
            {/* <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by Batch, Lot or Activity ID..."
                className="w-full pl-11 pr-4 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div> */}
            <div className="relative min-w-[500px]">
              {/* Input */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setOpen(true);
                  }}
                  onFocus={() => setOpen(true)}
                  placeholder="Search or select batch"
                  className="w-full pl-11 pr-10 py-3 rounded-2xl bg-slate-50 border border-slate-200 text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />

                <LuChevronDown
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 cursor-pointer"
                  onClick={() => setOpen(!open)}
                />
              </div>

              {/* Dropdown */}
              {open && (
                <div ref={dropdownRef} className="absolute z-50 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-lg max-h-64 overflow-auto">
                  {plateBatches
                    .filter((b) =>
                      b.label.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((b) => (
                      <div
                        key={b.value}
                        onClick={() => {
                          setSelectedBatchFilter(b.value);
                          setSearch(b.label);
                          setOpen(false);
                        }}
                        className="px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        {b.label}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <select
                value={selectedBatchFilter}
                onChange={e => setSelectedBatchFilter(e.target.value)}
                className="px-4 py-3 rounded-2xl bg-white border border-slate-200 text-xs font-semibold uppercase tracking-widest outline-none focus:border-blue-500 shadow-sm min-w-[160px]"
              >
                <option value="">All Batch Codes</option>
                {plateBatches.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={fromdate}
                  onChange={e => setFromDate(e.target.value)}
                  className="px-3 py-2.5 rounded-2xl bg-white border border-slate-200 text-xs font-medium outline-none focus:border-blue-500 shadow-sm"
                />
                <span className="text-slate-400 text-xs">—</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="px-3 py-2.5 rounded-2xl bg-white border border-slate-200 text-xs font-medium outline-none focus:border-blue-500 shadow-sm"
                />
              </div>
              <div className="h-10 w-[1px] bg-slate-200 mx-2" />
              <button onClick={handleFilterApply} className="px-6 py-3 rounded-2xl bg-blue-50 text-blue-600 text-xs font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                Apply Filter
              </button>
              <button onClick={() => { setSelectedBatchFilter(''); setSearch(''); setFromDate(new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]); setToDate(currentDate); setUseEffectRequired(true); }} className="w-11 h-11 flex items-center justify-center rounded-2xl bg-slate-50 border border-slate-100 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                <History className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="sticky top-[88px] bg-white border-b border-slate-200/60 z-20 overflow-x-auto no-scrollbar">
          <div className="max-w-[1600px] mx-auto flex">
            {[
              { id: 'OVERVIEW', icon: Activity, label: 'Global Overview' },
              { id: 'BATCH', icon: Box, label: 'Batch Accountability' },
              { id: 'LOT', icon: ClipboardList, label: 'Lot Reconciliation' },
              { id: 'ACTIVITY', icon: Zap, label: 'Utilization Context' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleSelectActivityTab(tab.id as string)}
                className={`flex items-center gap-4 px-8 py-3 text-xs font-black uppercase tracking-widest border-b-2 transition-all relative whitespace-nowrap ${activeTab === tab.id
                  ? 'text-blue-600 border-blue-600 bg-blue-50/30'
                  : 'text-slate-400 border-transparent hover:text-slate-600 hover:bg-slate-50'
                  }`}
              >
                <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'opacity-100' : 'opacity-40'}`} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <main className="max-w-[1600px] mx-auto p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {activeTab === 'OVERVIEW' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                    <KpiCard label="Total Batches" val={totalBatches.toLocaleString()} color="purple" icon={ShieldMinus} />
                    <KpiCard label="Total Inventory" val={totalInventory.toLocaleString()} color="orange" icon={Layers} />
                    <KpiCard label="Used" val={totalUsed.toLocaleString()} color="rose" icon={CheckCircle2} />
                    <KpiCard label="Remaining" val={totalRemaining.toLocaleString()} color="emerald" icon={Clock} />
                    <KpiCard label="Active Plates" val={totalActive.toLocaleString()} color="amber" icon={Zap} />
                    <KpiCard label="Rejected" val={totalRejected.toLocaleString()} color="rose" icon={FlaskConical} />
                    <KpiCard label="Variance" val={totalVariance.toLocaleString()} color="orange" icon={AlertCircle} />
                    <KpiCard label="Exceptions" val={totalExceptions.toLocaleString()} color="purple" icon={AlertTriangle} />
                    <KpiCard label="Expired" val={totalExpired.toLocaleString()} color="rose" icon={Calendar} />
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="xl:col-span-2">
                      <CompactReconSummary
                        assigned={totalInventory}
                        accounted={totalAccounted}
                        variance={totalVariance}
                        rejected={totalRejected}
                        exceptions={totalExceptions}
                        accuracy={overallAccuracy}
                      />
                    </div>
                    <div className="xl:col-span-1 bg-white p-4 px-6 rounded-3xl border border-slate-200/60 shadow-sm flex flex-col justify-center gap-1 h-[110px]">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Investigation Action Required</p>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                          <AlertCircle className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-xl font-black text-slate-800">{totalVariance} Plates Discrepancy</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{totalVariance > 0 ? 'Manual Reconciliation Required' : 'No Discrepancy Found'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <BatchStatusBanner
                    variance={batchesWithVariance}
                    exceptions={batchesWithExceptions}
                    reconciled={batchesReconciled}
                    pending={batchesPending}
                  />

                  <TableContainer title="Audit-Ready Reconciliation Registry" icon={Box}>
                    <thead>
                      <tr className="text-[13px] font-semibold text-slate-500 bg-slate-50/50">
                        <th className="pl-6 py-4 text-left">Batch ID</th>
                        <th className="py-4 text-left">Lots</th>
                        <th className="py-4 text-left text-[#1E3A8A]">Assigned</th>
                        <th className="py-4 text-left text-blue-600 font-bold">Accounted</th>
                        <th className="py-4 text-left text-emerald-600">Remaining</th>
                        <th className="py-4 text-left text-amber-600">Active</th>
                        <th className="py-4 text-left text-rose-500">Rejected</th>
                        <th className="py-4 text-left text-orange-500">Variance</th>
                        <th className="py-4 text-left text-purple-600">Excep.</th>
                        {/* <th className="py-4 text-left">Recon %</th> */}
                        {/* <th className="py-4 text-left">Audit Status</th> */}
                        <th className="py-4 pr-6 text-right">View</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100/60">
                      {batchListRaw.map((b) => (
                        <tr key={b.code} className="group hover:bg-slate-50/40 transition-all text-sm font-medium h-16">
                          <td className="pl-6 py-4">
                            <span className="text-slate-900 font-bold">{b.code}</span>
                          </td>
                          <td className="py-4 text-slate-600">{b.totalLots}</td>
                          <td className="py-4 text-[#1E3A8A] text-lg font-bold">{b.totalPlates}</td>
                          <td className="py-4 text-blue-600 text-lg font-bold">{b.accountedPlates}</td>
                          <td className="py-4 text-emerald-600 text-lg font-bold">{b.remainingPlates}</td>
                          <td className="py-4 text-amber-600 text-lg font-bold">{b.activePlates}</td>
                          <td className="py-4 text-rose-500 text-lg font-bold">{b.rejectedPlates}</td>
                          <td className={`py-4 text-lg font-bold ${b.variancePlates > 0 ? 'text-orange-500' : 'text-slate-300'}`}>
                            {b.variancePlates}
                          </td>
                          <td className={`py-4 text-lg font-bold ${b.exceptionPlates > 0 ? 'text-purple-600' : 'text-slate-300'}`}>
                            {b.exceptionPlates}
                          </td>
                          {/* <td className="py-4">
                            <span className={`px-2.5 py-1 rounded-lg text-[13px] font-black ${b.reconPercentage < 100 ? 'text-orange-600 bg-orange-50' : 'text-emerald-600 bg-emerald-50'}`}>
                              {b.reconPercentage}%
                            </span>
                          </td> */}
                          {/* <td className="py-4">
                            <ReconStatusBadge status={b.reconStatus} />
                          </td> */}
                          <td className="py-4 pr-6 flex justify-center items-center">
                            <button
                              onClick={() => handleBatchRowClick(b)}
                              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100 text-slate-400 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] transition-all ml-auto shadow-sm"
                            ><ChevronRight className="w-5 h-5" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </TableContainer>
                  <div>
                    <Pagination
                      page={page}
                      totalPages={totalPages}
                      handlePageChange={handlePageChange}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'BATCH' && (
                <div className="space-y-6">
                  {/* Batch Summary Header */}
                  <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200/60 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                    <div className="flex items-center gap-6">
                      <div className="w-16 h-16 rounded-3xl bg-blue-50 flex items-center justify-center text-blue-600 shadow-inner">
                        <Box className="w-8 h-8" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h2 className="text-2xl font-black tracking-tighter text-slate-900">{currentBatch.code}</h2>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-black uppercase rounded border border-blue-100">Active Reconciliation</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <DetailEntry l="Plate Media Type" v={currentBatch.plateType} mono />
                          <DetailEntry l="GRN Reference" v={currentBatch.grn} mono />
                          <DetailEntry l="Manufacturer" v={currentBatch.manufacturer} />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-6 pr-4">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                        <DetailEntry l="Manufacturing Date" v={helperFunctions.formatDate(currentBatch.mfgDate)} />
                        <DetailEntry l="Expiry Date" v={helperFunctions.formatDate(currentBatch.expiryDate)} alert />
                        <DetailEntry l="QA Approved" v={helperFunctions.formatDate(currentBatch.qaApprovedDate, true)} />
                        <DetailEntry l="TCM Status" v={currentBatch.tcmStatus} />
                      </div>
                      <div className="w-[1px] bg-slate-100 self-stretch" />
                      <DetailEntry l="Total Lots Created" v={currentBatch.totalLots.toString()} />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-3">
                          <KpiCard label="Assigned Plates" val={currentBatch.totalPlates} color="slate" icon={Box} />
                          <KpiCard label="Used (Accounted)" val={currentBatch.usedPlates} color="purple" icon={CheckCircle2} />
                          <KpiCard label="Remaining Inventory" val={currentBatch.remainingPlates} color="emerald" icon={Clock} />
                          <KpiCard label="Variance detected" val={currentBatch.variancePlates} color="orange" icon={AlertCircle} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm space-y-6">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] border-b border-slate-50 pb-3">Batch Accountability Summary</h3>
                            <div className="grid grid-cols-3 gap-4">
                              {[
                                { l: 'Accounted', v: currentBatch.accountedPlates, c: 'text-purple-600' },
                                { l: 'Variance', v: currentBatch.variancePlates, c: 'text-orange-500' },
                                { l: 'Accuracy', v: `${currentBatch.reconPercentage}%`, c: 'text-emerald-600' }
                              ].map((m, idx) => (
                                <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center">
                                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">{m.l}</span>
                                  <p className={`text-lg font-black ${m.c}`}>{m.v}</p>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm space-y-4">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] border-b border-slate-50 pb-3 flex items-center justify-between">
                              <span>Investigation Metrics</span>
                              <AlertCircle className="w-4 h-4 text-orange-400" />
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                              <KpiCard label="In-Process" val={currentBatch.activePlates} color="purple" />
                              <KpiCard label="Exceptions" val={currentBatch.exceptionPlates} color="purple" />
                              <KpiCard label="Rejected" val={currentBatch.rejectedPlates} color="rose" />
                              <KpiCard label="Expired" val={currentBatch.expiredPlates} color="amber" />
                            </div>
                          </div>
                        </div>
                      </div>

                      <ActivityDonutChart data={activityChartData} />
                    </div>

                    <TableContainer title="Lot Accountability Investigation" icon={ClipboardList}>
                      <thead>
                        <tr className="text-[13px] font-semibold text-slate-500 bg-slate-50/50">
                          <th className="pl-6 py-4">Lot ID</th>
                          <th className="py-4 text-[#1E3A8A]">Assigned</th>
                          <th className="py-4 text-blue-600">Accounted</th>
                          <th className="py-4 text-emerald-600">Remaining</th>
                          <th className="py-4 text-orange-500">Variance</th>
                          <th className="py-4 text-rose-500">Rejected</th>
                          <th className="py-4">Accuracy</th>
                          <th className="py-4">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100/50">
                        {lotListRaw.map((lot, i) => (
                          <tr key={i} className="group hover:bg-slate-50/50 transition-all text-sm font-medium h-16">
                            <td className="pl-6 py-4 text-slate-900 font-bold">{lot.code}</td>
                            <td className="py-4 text-[#1E3A8A] text-lg font-bold">{lot.totalPlates}</td>
                            <td className="py-4 text-blue-600 text-lg font-bold">{lot.usedPlates}</td>
                            <td className="py-4 text-emerald-600 text-lg font-bold">{lot.remainingPlates}</td>
                            <td className="py-4 text-orange-500 text-lg font-bold">{lot.variancePlates}</td>
                            <td className="py-4 text-rose-500 text-lg font-bold">{lot.rejectedPlates}</td>
                            <td className="py-4 min-w-[140px]">
                              <ProgressBarCompact val={lot.usedPlates} total={lot.totalPlates} color="blue" />
                            </td>
                            <td className="py-4">
                              <button
                                onClick={() => handleLotInvestigate(lot)}
                                className="text-blue-600 uppercase tracking-widest text-[10px] font-black hover:underline"
                              >Investigate</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </TableContainer>
                    <div>
                      <Pagination
                        page={lotWisePage}
                        totalPages={lotWiseTotalPages}
                        handlePageChange={handleLotWisePageChangeLot}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'LOT' && (
                <div className="space-y-6">
                  {/* Lot Header & Stats Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm space-y-6">
                      <div className="flex items-center gap-4 border-b border-slate-50 pb-4">
                        <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600">
                          <ClipboardList className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="text-xl font-black text-slate-900 leading-none">{currentLot.code}</h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Lot Reconciliation Identification</p>
                        </div>
                        <div className="ml-auto">
                          <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase ${currentLot.samplingStatus === 'In-Process' ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>
                            {currentLot.samplingStatus}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                        <DetailEntry l="Parent Batch" v={currentBatch.code} mono />
                        <DetailEntry l="Media / Plate Type" v={currentBatch.plateType} />
                        <DetailEntry l="Expiry Date" v={helperFunctions.formatDate(currentBatch.expiryDate)} alert />
                        <DetailEntry l="Last Actionable Sync" v={currentLot.lastSync} />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setActiveTab('BATCH')}
                          className="flex-1 py-3 rounded-2xl bg-slate-50 text-slate-600 text-[10px] font-black uppercase tracking-[0.2em] border border-slate-200 hover:bg-slate-100 transition-all flex items-center justify-center gap-2"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" /> Batch Details
                        </button>
                        {/* <button className="flex-1 py-3 rounded-2xl bg-[#0F172A] text-white text-[10px] font-black uppercase tracking-[0.2em] hover:bg-slate-800 transition-all flex items-center justify-center gap-2">
                          Export Audit <Download className="w-3.5 h-3.5" />
                        </button> */}
                      </div>
                    </div>

                    <div className="lg:col-span-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 h-full">
                        <div className="h-full"><KpiCard label="Assigned" val={currentLot.totalPlates} color="emerald" icon={Box} /></div>
                        <div className="h-full"><KpiCard label="Accounted" val={currentLot.usedPlates} color="purple" icon={CheckCircle2} /></div>
                        <div className="h-full"><KpiCard label="Remaining" val={currentLot.remainingPlates} color="emerald" icon={Clock} /></div>
                        <div className="h-full"><KpiCard label="Variance" val={currentLot.variancePlates} color="orange" icon={AlertCircle} /></div>

                        <div className="h-full"><KpiCard label="Rejected" val={currentLot.rejectedPlates} color="rose" icon={FlaskConical} /></div>
                        <div className="h-full"><KpiCard label="QR Generated" val={currentLot.qrGenerated} color="blue" icon={QrCode} /></div>
                        <div className="h-full"><KpiCard label="QR Pending" val={currentLot.qrPending} color="amber" icon={Clock} /></div>
                        <div className="h-full"><KpiCard label="Utilization" val={`${currentLot.utilization}%`} color="purple" icon={Activity} /></div>
                      </div>
                    </div>
                  </div>

                  {/* Activity Wise Details Table */}
                  <TableContainer title="Activity-wise Accountability Details" icon={Activity}>
                    <thead>
                      <tr className="text-[13px] font-semibold text-slate-500 bg-slate-50/50">
                        <th className="pl-6 py-4 text-left">Activity Description</th>
                        <th className="py-4 text-left">Assigned</th>
                        <th className="py-4 text-left text-blue-600">Accounted</th>
                        <th className="py-4 text-left text-emerald-600">Remaining</th>
                        <th className="py-4 text-left text-orange-500">Variance</th>
                        <th className="py-4 text-left text-rose-500">Rejected</th>
                        <th className="py-4 text-left text-purple-600">Excep.</th>
                        <th className="py-4 ">Audit Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100/60">
                      {activityWiseListRaw.map((act, i) => (
                        <tr key={i} className="group hover:bg-slate-50/40 transition-all text-sm font-medium h-16">
                          <td className="pl-6 py-4 text-slate-900 font-bold">{act.activity}</td>
                          <td className="py-4 text-slate-600 text-lg font-bold">{act.assigned}</td>
                          <td className="py-4 text-blue-600 text-lg font-bold">{act.used}</td>
                          <td className="py-4 text-emerald-600 text-lg font-bold">{act.remaining}</td>
                          <td className={`py-4 text-lg font-bold ${act.variance > 0 ? 'text-orange-500' : 'text-slate-300'}`}>
                            {act.variance}
                          </td>
                          <td className={`py-4 text-lg font-bold ${act.rejected > 0 ? 'text-rose-500' : 'text-slate-300'}`}>
                            {act.rejected}
                          </td>
                          <td className={`py-4 text-lg font-bold ${act.exceptions > 0 ? 'text-purple-600' : 'text-slate-300'}`}>
                            {act.exceptions}
                          </td>
                          <td className="py-4">
                            <span className={`px-2.5 py-1 rounded-lg text-[11px] font-black uppercase ${act.status === 'Released' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                              'bg-orange-50 text-orange-600 border border-orange-100'
                              }`}>{act.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </TableContainer>
                  <div>
                    <Pagination
                      page={activityWisePage}
                      totalPages={activityWiseTotalPages}
                      handlePageChange={handleActivityWisePageChange}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'ACTIVITY' && (
                <div className="space-y-6">
                  {/* Row 1: High Level KPI Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <KpiCard label="Total Inventory" val={totalInventory.toLocaleString()} color="slate" icon={Box} />
                    <KpiCard label="QR Generated" val={totalQrGenerated.toLocaleString()} color="blue" icon={QrCode} />
                    <KpiCard label="Pending QR" val={totalQrPending.toLocaleString()} color="amber" icon={Clock} />
                    <KpiCard label="Global Remaining" val={totalRemainingInActivity.toLocaleString()} color="emerald" icon={Activity} />
                  </div>

                  {/* Row 2: Inventory Details by Batch & Lot */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {/* Batch Remaining Summary */}
                    <div>
                      <TableContainer title="Inventory Details by Batch" icon={Box}>
                        <thead>
                          <tr className="text-[11px] font-black text-slate-500 bg-slate-50/50 uppercase tracking-widest">
                            <th className="pl-6 py-4 text-left">Batch ID</th>
                            <th className="py-4 text-left">Total</th>
                            <th className="py-4 text-left">QR Ref.</th>
                            <th className="py-4 text-left text-emerald-600">Rem.</th>
                            <th className="py-4 ">View</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/60">
                          {batchListRaw.map((b) => (
                            <tr
                              key={b.code}
                              onClick={() => { setSelectedBatchCode(b.code); handleBatchRemainingClick(b); }}
                              className={`group cursor-pointer transition-all text-xs font-medium h-14 ${selectedBatchCode === b.code ? 'bg-blue-50/50' : 'hover:bg-slate-50/40'}`}
                            >
                              <td className="pl-6 py-4 font-bold text-slate-900">{b.code}</td>
                              <td className="py-4 text-slate-600 font-bold">{b.totalPlates}</td>
                              <td className="py-4">
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-blue-600 font-black tracking-tight">{b.totalPlates - b.remainingPlates} Generated</span>
                                  <span className="text-[9px] text-amber-500 font-bold">{b.remainingPlates} Pending</span>
                                </div>
                              </td>
                              <td className="py-4 text-emerald-600 font-black text-sm">{b.remainingPlates}</td>
                              <td className="py-4">
                                <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${selectedBatchCode === b.code ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'}`}>
                                  <ChevronRight className="w-3.5 h-3.5" />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </TableContainer>
                      <div className='mt-4'>
                        <Pagination
                          page={page}
                          totalPages={totalPages}
                          handlePageChange={handlePageChange}
                        />
                      </div>
                    </div>

                    {/* Lot Remaining Summary (Linked to Batch Selection) */}
                    <TableContainer title={`Remaining Lots: ${selectedBatchCode || 'Select Batch'}`} icon={ClipboardList}>
                      <thead>
                        <tr className="text-[11px] font-black text-slate-500 bg-slate-50/50 uppercase tracking-widest">
                          <th className="pl-6 py-4 text-left">Lot ID</th>
                          <th className="py-4 text-left">Assigned</th>
                          <th className="py-4 text-left text-emerald-600">Remaining</th>
                          <th className="py-4 pr-6 text-right">Utilization</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100/60">
                        {remainingPlatesRaw.map((lot) => (
                          <tr key={lot.code} className="group hover:bg-slate-50/40 transition-all text-xs font-medium h-14">
                            <td className="pl-6 py-4 font-bold text-slate-900">{lot.code}</td>
                            <td className="py-4 text-slate-600 font-bold">{lot.totalPlates}</td>
                            <td className="py-4 text-emerald-600 font-black text-sm">{lot.remainingPlates}</td>
                            <td className="py-4 pr-6 text-right w-24">
                              <ProgressBarCompact val={lot.totalPlates - lot.remainingPlates} total={lot.totalPlates} color="blue" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </TableContainer>
                  </div>

                  {/* Row 3: Activity Breakdown & Detailed Metrics */}
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <div className="lg:col-span-1">
                      <ActivityDonutChart data={activityChartData} />
                    </div>

                    <div className="lg:col-span-3">
                      <TableContainer title="Contextual Activity Metrics Breakdown" icon={Zap}>
                        <thead>
                          <tr className="text-[13px] font-semibold text-slate-500 bg-slate-50/50">
                            <th className="pl-6 py-4 text-left">Activity Description</th>
                            <th className="py-4 text-left">Assigned</th>
                            <th className="py-4 text-left text-blue-600">Used</th>
                            <th className="py-4 text-left text-emerald-600">Remaining</th>
                            <th className="py-4 text-left text-rose-500">Rejected</th>
                            <th className="py-4 ">Audit Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/60">
                          {activityWiseListRaw.map((act, i) => (
                            <tr key={i} className="group hover:bg-slate-50/40 transition-all text-sm font-medium h-16">
                              <td className="pl-6 py-4 text-slate-900 font-bold">{act.activity}</td>
                              <td className="py-4 text-slate-600 font-bold">{act.assigned}</td>
                              <td className="py-4 text-blue-600 font-bold">{act.used}</td>
                              <td className="py-4 text-emerald-600 font-bold">{act.remaining}</td>
                              <td className="py-4 text-rose-500 font-bold">{act.rejected}</td>
                              <td className="py-4">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase ${act.status === 'Released' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'
                                  }`}>{act.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </TableContainer>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}